import {
  FIELDS_CHECK_IN_LOG,
  FIELDS_COMPANION,
  FIELDS_GUEST,
  FIELDS_REGISTRANT,
} from "@/lib/utils/default-returner";

/**
 * ONE answer to "which guest columns does a CUSTOM role's `fieldAllowlist`
 * govern?" — shared by the template editor (what the picker offers), the
 * view-import lane (what a saved view maps to) and `FieldsProvider` (what a
 * restricted role gets to see).
 *
 * Before this, the editor carried a hand-copied list of 24 keys while the guest
 * family knows ~56 built-in columns. Everything outside the copy — the check-in
 * action column, `createdAt`, `companions`, `seating`, `slotting`, the activity
 * rollups, … — could never be granted to a restricted role, and the "Aktion"
 * column simply vanished for it with no way to bring it back.
 */

/**
 * Columns a `fieldAllowlist` never governs. Two kinds:
 *
 *  - **Structural identifiers** (`guestId`, `id`): the row needs them for React
 *    keys, selection and the detail panel; the backend force-projects `guestId`
 *    regardless (`ProjectionBuilder.intersectTopLevel`).
 *  - **Presentation-only action columns** (`checkInOutCTA`, `actionRegistrant`,
 *    `actionCompanion`): they carry no guest DATA — the cell renders a button
 *    whose visibility is the CAPABILITY's decision (`guests:check-in`,
 *    `guests:write`). Storing them on the allowlist would make the same action
 *    depend on two switches, and hide it whenever the admin forgot the second.
 *    Same reasoning for the guest popup's layout-only portal-link cell
 *    (`guestPortalLink` = `PORTAL_LINK_LAYOUT_KEY` in guestDetailLayout, a
 *    literal here so lib/authz does not import a component module; a test
 *    ties the two): a mint button, gated like the header action on
 *    `shared-links:write` + `attachments:read` + `guests:read.qr-token`.
 *
 * None of these are offered by the picker, stored on a template, or filtered
 * out by `FieldsProvider`.
 */
export const ALLOWLIST_EXEMPT_FIELDS: ReadonlySet<string> = new Set([
  "guestId",
  "id",
  "checkInOutCTA",
  "actionRegistrant",
  "actionCompanion",
  "guestPortalLink",
  // plan arrival-automation Rev. 4 §1.8: the guest popup's "Ankunft melden an"
  // cell (`guestArrivalNotice` = `ARRIVAL_NOTICE_LAYOUT_KEY`, a literal like
  // the portal key) — OWNER / ADMIN decide, like for the contacts it shows
  // (the backend's `authorize()`), never a field allowlist.
  "guestArrivalNotice",
]);

export const isAllowlistExempt = (fieldName: string): boolean =>
  ALLOWLIST_EXEMPT_FIELDS.has(fieldName);

/** The prefix a CUSTOM field wears on an allowlist. */
export const ALLOWLIST_ADDITIONAL_PREFIX = "additionalFields.";

/** The minimum a field has to be for the allowlist to have an opinion about it. */
export interface AllowlistCandidate {
  name: string;
  /** True for a custom field (it is addressed as `additionalFields.<name>`). */
  additionalField?: boolean;
}

/** The key an allowlist would carry for this field. */
export const allowlistKeyOf = (field: AllowlistCandidate): string =>
  field.additionalField ? `${ALLOWLIST_ADDITIONAL_PREFIX}${field.name}` : field.name;

/**
 * Whether a CUSTOM role holding `allowSet` may SEE this field. `null` means
 * unrestricted.
 *
 * This is the one answer `FieldsProvider` derives `visibleFields` from and
 * the one the field-definition EXPORT filters its rows against — the export
 * reads the definitions fresh through the widest projection, so without this
 * a role that cannot see an Art. 9 field on any screen could still download
 * its name, options, bounds, formula and sensitivity.
 */
export const isFieldAllowed = (
  field: AllowlistCandidate,
  allowSet: ReadonlySet<string> | null,
): boolean => {
  if (!allowSet) return true;
  // The exempt names are BUILT-IN columns: a custom field an admin happens to
  // call `guestPortalLink` / `checkInOutCTA` is guest data and needs its
  // `additionalFields.<name>` key like any other (audit r1 blast F3).
  if (!field.additionalField && ALLOWLIST_EXEMPT_FIELDS.has(field.name)) return true;
  return allowSet.has(allowlistKeyOf(field));
};

/** A lookup set for `isFieldAllowed`; `null`/`undefined` stays `null` (unrestricted). */
export const allowlistLookup = (
  list: readonly string[] | null | undefined,
): ReadonlySet<string> | null => (list == null ? null : new Set(list));

/**
 * Guest data that has no column of its own but IS masked by the allowlist
 * (`GuestFieldMasker` strips every top-level key it does not name), so a
 * restricted role can only see it when the template grants it explicitly:
 *  - `acceptanceState` — the organiser's decision on a registration/companion
 *    (the registrations "Aktion" cell reads it to know what it may offer).
 *
 * `emailUndeliverable(At)` used to be listed here too; since they became
 * guest-list columns they arrive through `FIELDS_GUEST` like every other
 * built-in (the Set below dedupes either way).
 */
const ALLOWLISTABLE_EXTRAS = ["acceptanceState"];

/**
 * Every built-in guest-family column a template may put on its allowlist —
 * derived from the live catalogues (guest list ∪ registrations ∪ companions),
 * so a column added to `FIELDS_GUEST` is grantable the same day, minus the
 * exempt keys above. Custom fields are NOT in here: they join as
 * `additionalFields.<name>` from the event's own field schema.
 */
export const ALLOWLISTABLE_GUEST_FIELDS: readonly string[] = Array.from(
  new Set(
    [...FIELDS_GUEST, ...FIELDS_REGISTRANT, ...FIELDS_COMPANION]
      .map((f) => f.name)
      .concat(ALLOWLISTABLE_EXTRAS),
  ),
).filter((name) => !ALLOWLIST_EXEMPT_FIELDS.has(name));

const ALLOWLISTABLE_SET: ReadonlySet<string> = new Set(ALLOWLISTABLE_GUEST_FIELDS);

/**
 * Whether an allowlist KEY (bare built-in name or `additionalFields.<name>`)
 * is one a template can store meaningfully.
 */
export const isAllowlistableKey = (key: string): boolean =>
  key.startsWith("additionalFields.") || ALLOWLISTABLE_SET.has(key);

/**
 * Column key → the backend projection tokens its CELL reads.
 *
 * The guest list asks the backend for a fixed field string (`GUEST_FIELDS`)
 * and the gateway intersects it against the allowlist BY NAME, before the list
 * service decides whether to run the seat/appointment/main-guest lookups at all.
 * The "Sitzplatz" column is `seating`, but the row's seat arrives as `seatInfo`
 * — so a template holding only `seating` showed the column with every guest
 * "unseated" (same for `slotting`/`appointments`, and a companion row's
 * "Begleitung von …" via `mainGuestDTO`). The tokens ride along with their
 * column on the wire and stay invisible in the picker.
 */
export const ALLOWLIST_KEY_IMPLIES: Readonly<Record<string, readonly string[]>> = {
  seating: ["seatInfo"],
  slotting: ["appointments"],
  companions: ["mainGuestDTO"],
};

const IMPLIED_TOKENS: ReadonlySet<string> = new Set(
  Object.values(ALLOWLIST_KEY_IMPLIES).flat(),
);

/** A wire token that rides with a column — never shown, never picked. */
export const isImpliedAllowlistToken = (key: string): boolean =>
  IMPLIED_TOKENS.has(key);

/**
 * Canonical wire shape of an allowlist: every column's projection tokens
 * present exactly once, right after their column; stray tokens whose column
 * is gone dropped. Idempotent. `null` (unrestricted) and `[]` (fail-closed)
 * pass through untouched.
 */
export function normalizeAllowlist(
  list: string[] | null | undefined,
): string[] | null {
  // `== null`: an absent list (undefined) is "unrestricted" exactly like null —
  // the preview store's scope key used to tolerate it, so this must too.
  if (list == null) return null;
  const out: string[] = [];
  const seen = new Set<string>();
  for (const key of list) {
    if (isImpliedAllowlistToken(key) || seen.has(key)) continue;
    seen.add(key);
    out.push(key);
    // `hasOwnProperty`, not a bare index: the keys come from a STORED list and
    // the backend validates them by regex only, so `constructor`, `toString`,
    // `valueOf` or `__proto__` can sit in one. A bare index answers those from
    // Object.prototype with a function — not nullish, so `?? []` does not fire
    // and `for…of` throws. This function now runs on every read AND write path
    // of the role-template editor, which has no local error boundary.
    const implied = Object.prototype.hasOwnProperty.call(ALLOWLIST_KEY_IMPLIES, key)
      ? ALLOWLIST_KEY_IMPLIES[key]
      : [];
    for (const token of implied) {
      if (seen.has(token)) continue;
      seen.add(token);
      out.push(token);
    }
  }
  return out;
}

/**
 * The check-in log's columns that COPY a guest column, and that column — the
 * mirror of main-backend's `CheckInLogRepository.guestColumnOf`. Every other
 * log column (action, createdAt, performedByType, deviceName, programItemName,
 * note) is the log's own and no guest allowlist governs it.
 *
 * `checkInLogCTA` is no column of the backend's, but it renders the guest's
 * status as its label ("Check in" / "Check out", read from checkInOutStatus):
 * it follows `checkedIn` like the status column itself (round-2 audit MED-A).
 */
const CHECK_IN_LOG_GUEST_COLUMN: Readonly<Record<string, string>> = {
  guestFirstName: "firstName",
  guestLastName: "lastName",
  guestEmail: "email",
  checkInOutStatus: "checkedIn",
  checkInLogCTA: "checkedIn",
};

/**
 * A CUSTOM role's guest-column allowlist translated for the check-in history's
 * catalogue (the log's own columns merged with the event's guest fields).
 *
 * The raw allowlist names guest columns only, so handed to that catalogue as-is
 * it hid every log column — `action`, `createdAt`, the device, and the status
 * even for a role that may read `checkedIn`. Here a log column that copies a
 * guest column follows that column's grant, a log-native column is always
 * allowed, and the merged guest fields keep following the allowlist as they
 * are — the rule the backend applies to the log's filter, sort and search.
 * `null`/`undefined` (unrestricted) stays `null`.
 */
export function checkInLogAllowlist(
  guestAllowlist: readonly string[] | null | undefined,
): string[] | null {
  if (guestAllowlist == null) return null;
  const granted = new Set(guestAllowlist);
  const logColumns = FIELDS_CHECK_IN_LOG.map((f) => f.name).filter((name) => {
    const guestColumn = CHECK_IN_LOG_GUEST_COLUMN[name];
    return guestColumn === undefined || granted.has(guestColumn);
  });
  return [...new Set([...guestAllowlist, ...logColumns])];
}
